import { useRef } from "react";

/**
 * Backdrop-dismiss handlers for a modal that closes on an outside click.
 *
 * Naively wiring `onClick={onClose}` on the backdrop closes the modal whenever
 * a `click` lands on it — including the synthetic click that fires when you
 * start a text selection INSIDE the card, drag the cursor out, and release on
 * the backdrop (the browser dispatches that click on the nearest common
 * ancestor, i.e. the backdrop, so the card's `stopPropagation` never runs).
 * Selecting a phone number and letting go outside the popup would then close it.
 * Wiring `onMouseDown` alone is wrong the other way round: since the backdrop
 * became the scroll container for a tall card (membership-per-credit-revoke,
 * 2026-10-29), a press on the overlay's OWN scrollbar is a mousedown on the
 * backdrop with no `click` after it, and it closed the modal mid-scroll.
 *
 * Fix: only dismiss on a TRUE outside click — the press AND the release both on
 * the backdrop itself. We record whether `mousedown` started on the backdrop
 * and only call `onClose` if the subsequent `click` also targets the backdrop.
 * A press that begins inside the card never arms the flag, so releasing on the
 * backdrop (text-selection drag-out) leaves the modal open; a scrollbar press
 * arms it but never clicks, so it leaves the modal open too.
 *
 * Lived in `pages/Data.tsx` from 2026-09 until the review of 2026-10-29 found
 * `MetaModal` and `ActionModalButton` dismissing on the bare mousedown; one
 * module now, every `.modal-backdrop` spreads it.
 */
export function createBackdropDismiss(
  onClose: () => void,
  pressedOnBackdrop: { current: boolean },
): {
  onMouseDown: (e: { target: unknown; currentTarget: unknown }) => void;
  onClick: (e: { target: unknown; currentTarget: unknown }) => void;
} {
  return {
    onMouseDown: (e): void => {
      pressedOnBackdrop.current = e.target === e.currentTarget;
    },
    onClick: (e): void => {
      if (pressedOnBackdrop.current && e.target === e.currentTarget) {
        onClose();
      }
      pressedOnBackdrop.current = false;
    },
  };
}

export function useBackdropDismiss(onClose: () => void): {
  onMouseDown: (e: React.MouseEvent) => void;
  onClick: (e: React.MouseEvent) => void;
} {
  const pressedOnBackdrop = useRef(false);
  return createBackdropDismiss(onClose, pressedOnBackdrop);
}
